/**
 * A minimal JSON-Schema → form model (spec §6.2 — one form engine, zero hand-coded per-capability
 * forms). Two sources feed it: (1) the structured IR node fields (decision/humanTask/join/scoring/…)
 * whose shapes are fixed by the ratified flow-ir schema, authored here; (2) a capability provider's
 * `configSchema` (JSON-Schema-style) mapped at runtime when available. No heavy deps — a small
 * subset renderer (SchemaForm.tsx) draws AntD controls from these descriptors.
 */
import type { NodeKind } from '@/theme/tokens';

export type FieldType =
  | 'string'
  | 'text'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'stringList'
  | 'json';

export interface FieldSchema {
  /** Dotted path into the node object (e.g. 'name', 'flow.id', 'config.threshold'). */
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  required?: boolean;
  enumValues?: string[];
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

// ------------------------------------------------------------------------------------------------
// Path get/set (immutable) — the form edits nested node fields (flow.id, prompt.template, config.*).
// ------------------------------------------------------------------------------------------------

export function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[k];
    return undefined;
  }, obj);
}

/** Immutable set-by-path; deleting when value is undefined. Returns a new object graph. */
export function setPath<T extends Record<string, unknown>>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const next: Record<string, unknown> = { ...(obj as Record<string, unknown>) };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const existing = cursor[k];
    cursor[k] = existing && typeof existing === 'object' ? { ...(existing as object) } : {};
    cursor = cursor[k] as Record<string, unknown>;
  }
  const last = keys[keys.length - 1];
  if (value === undefined) delete cursor[last];
  else cursor[last] = value;
  return next as T;
}

// ------------------------------------------------------------------------------------------------
// JSON-Schema adapter (capability provider configSchema → FieldSchema[]).
// ------------------------------------------------------------------------------------------------

interface JsonSchemaProp {
  type?: string | string[];
  description?: string;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  items?: { type?: string };
  title?: string;
}

interface JsonSchemaObject {
  properties?: Record<string, JsonSchemaProp>;
  required?: string[];
}

function mapType(p: JsonSchemaProp): FieldType {
  if (Array.isArray(p.enum) && p.enum.length > 0) return 'enum';
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== 'null') : p.type;
  switch (t) {
    case 'integer':
      return 'integer';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return p.items?.type === 'string' ? 'stringList' : 'json';
    case 'object':
      return 'json';
    default:
      return 'string';
  }
}

/** Map a JSON-Schema object (properties) to FieldSchema[], keyed under a base path (e.g. 'config'). */
export function fieldsFromJsonSchema(schema: unknown, basePath = 'config'): FieldSchema[] {
  const js = schema as JsonSchemaObject | undefined;
  if (!js?.properties) return [];
  const required = new Set(js.required ?? []);
  return Object.entries(js.properties).map(([name, p]) => ({
    key: `${basePath}.${name}`,
    label: p.title ?? name,
    type: mapType(p),
    description: p.description,
    required: required.has(name),
    enumValues: p.enum?.map((v) => String(v)),
    min: p.minimum,
    max: p.maximum,
  }));
}

// ------------------------------------------------------------------------------------------------
// Structured IR-node field descriptors (the ratified flow-ir node shapes).
// ------------------------------------------------------------------------------------------------

const IDENTITY: FieldSchema = {
  key: 'name',
  label: 'Name',
  type: 'string',
  description: 'Display label for the node (the id is the stable identity).',
};

/** The kind-specific structured fields (excluding id/kind/stage, which the Inspector handles). */
export function coreFieldsForKind(kind: NodeKind): FieldSchema[] {
  switch (kind) {
    case 'decision':
      return [
        IDENTITY,
        { key: 'expression', label: 'Expression (CEL)', type: 'text', required: true, description: 'Deterministic rule (ADR-011).' },
        { key: 'outcomes', label: 'Outcomes', type: 'stringList', required: true, description: 'Closed outcome set (min 2). Each is a status-routable branch.' },
      ];
    case 'humanTask':
    case 'humanReviewTask':
      return [
        IDENTITY,
        { key: 'reviewerPool', label: 'Reviewer pool', type: 'string', required: true },
        { key: 'sla', label: 'SLA', type: 'string', required: true, placeholder: 'PT48H', description: 'ISO-8601 duration.' },
        { key: 'decisionOptions', label: 'Decisions', type: 'stringList', required: true, description: 'Closed decision vocabulary (min 2).' },
        { key: 'makerChecker', label: 'Maker-checker (4-eyes)', type: 'boolean' },
      ];
    case 'join':
      return [
        IDENTITY,
        { key: 'policy', label: 'Join policy', type: 'enum', required: true, enumValues: ['ALL', 'ANY', 'QUORUM'] },
        { key: 'quorum', label: 'Quorum', type: 'integer', min: 1, description: 'Required when policy is QUORUM.' },
      ];
    case 'subFlow':
      return [
        IDENTITY,
        { key: 'flow.id', label: 'Referenced flow id', type: 'string', required: true },
        { key: 'flow.version', label: 'Version', type: 'integer', min: 1, required: true },
        { key: 'entityBinding', label: 'Entity role', type: 'string', required: true, placeholder: 'HAS_UBO', description: 'Runs once per entity in this role.' },
      ];
    case 'dynamicBranch':
      return [
        IDENTITY,
        { key: 'over', label: 'List to fan out over', type: 'text', required: true, placeholder: 'nodes.registry.directors', description: 'A path to a list in an earlier step. One child runs per item. A plain path lets each child read its own item from the evidence.' },
        { key: 'elementKey', label: 'Item key', type: 'string', placeholder: 'element.din', description: 'What identifies one item, read from that item alone. Repeated keys run once. Needed when the output holds less than the evidence.' },
        { key: 'flow.id', label: 'Flow to run per item', type: 'string', required: true },
        { key: 'flow.version', label: 'Version', type: 'integer', min: 1, required: true },
        { key: 'entityBinding', label: 'Entity role', type: 'string', required: true, placeholder: 'HAS_DIRECTOR' },
        { key: 'policy', label: 'Finish when', type: 'enum', required: true, enumValues: ['ALL', 'ANY', 'QUORUM'], description: 'ALL: every child succeeds. ANY: the first success. QUORUM: that many succeed. Children no longer needed are cancelled.' },
        { key: 'successOutcomes', label: 'Outcomes that count as success', type: 'stringList', placeholder: 'All but REJECTED', description: 'The end-step outcomes of the child flow that count as a success. Leave empty and every outcome counts except REJECTED. A child that fails or is cancelled never counts.' },
        { key: 'quorum', label: 'Quorum', type: 'integer', min: 1, description: 'Required when finishing on QUORUM. At most the maximum children.' },
        { key: 'guardrails.maxFanOut', label: 'Maximum children', type: 'integer', min: 1, max: 500, required: true, description: 'A longer list starts no children and takes the LIMIT_REACHED route.' },
        { key: 'guardrails.maxDepth', label: 'Maximum nesting', type: 'integer', min: 1, max: 10, required: true, description: 'How deep fan-outs inside the child flow may go.' },
        { key: 'guardrails.budget', label: 'Budget (metering units)', type: 'integer', min: 1, required: true, description: 'Counts the whole run so far, not only the children. Checked before each start and as children finish, so running children can overshoot it.' },
        { key: 'guardrails.onLimit', label: 'When a limit is reached', type: 'enum', required: true, enumValues: ['FAIL', 'HUMAN_TASK'], description: 'FAIL routes LIMIT_REACHED to an end step. HUMAN_TASK routes it to a human task.' },
      ];
    case 'multiSourceScoring':
      return [
        IDENTITY,
        { key: 'threshold', label: 'Threshold', type: 'number', min: 0, max: 1, step: 0.01, required: true },
        { key: 'sources', label: 'Weighted sources', type: 'json', required: true, description: 'Array of {source, weight}; weights must sum to 1.0.' },
      ];
    case 'terminal':
      return [
        IDENTITY,
        { key: 'outcome', label: 'Outcome', type: 'string', required: true },
      ];
    case 'llmTask':
      return [
        IDENTITY,
        { key: 'pathType', label: 'Path type', type: 'enum', required: true, enumValues: ['ASSISTIVE', 'ADVERSARIAL'], description: 'ASSISTIVE degrades to a value; ADVERSARIAL fails closed (ADR-006).' },
        { key: 'fallback.mode', label: 'Fallback mode', type: 'enum', required: true, enumValues: ['DETERMINISTIC_VALUE', 'FAIL_CLOSED'] },
        { key: 'fallback.value', label: 'Fallback value', type: 'json', description: 'Required non-empty for DETERMINISTIC_VALUE; forbidden for FAIL_CLOSED.' },
        { key: 'prompt.id', label: 'Prompt id', type: 'string', required: true },
        { key: 'prompt.template', label: 'Prompt template', type: 'text', required: true },
        { key: 'params', label: 'Params', type: 'json', description: 'Model params + governance declarations (envelopeId, citeOrShutUp, shadow).' },
      ];
    case 'confidenceGate':
      return [
        IDENTITY,
        { key: 'source', label: 'Gated LLM task', type: 'string', required: true, description: 'The upstream llmTask node id whose recorded confidence is gated.' },
        { key: 'threshold', label: 'Threshold', type: 'number', min: 0, max: 1, step: 0.01, required: true },
      ];
    case 'parallel':
      return [IDENTITY];
    case 'capability':
    default:
      return [IDENTITY];
  }
}

/** Idempotency sub-form (all capability-ish nodes). */
export const IDEMPOTENCY_FIELDS: FieldSchema[] = [
  { key: 'idempotency.mode', label: 'Idempotency', type: 'enum', enumValues: ['NATURALLY_IDEMPOTENT', 'IDEMPOTENT_BY_KEY', 'AT_MOST_ONCE'] },
  { key: 'idempotency.key', label: 'Idempotency key (CEL)', type: 'string', description: 'Required when mode is IDEMPOTENT_BY_KEY.' },
];

/** Governance fields the author can edit inline (obligations/skippable/visibility). */
export const GOVERNANCE_FIELDS: FieldSchema[] = [
  { key: 'obligations', label: 'Obligations (REG-##)', type: 'stringList', description: 'Any diff touching a tagged construct is change-class C4.' },
  { key: 'skippable', label: 'Skippable', type: 'boolean', description: 'SKIPPED is excluded from roll-up.' },
  { key: 'visibility', label: 'Visibility', type: 'enum', enumValues: ['ALL', 'DATA_ROOM', 'DASHBOARD'] },
];
